// src/components/ForgotPassword.js
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import API_BASE_URL from '../config';
import { Container, Row, Col, Form, Button, Alert, Spinner } from 'react-bootstrap';
import Footer from './Footer';
import './ForgotPassword.css';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [resetLink, setResetLink] = useState('');
  const [emailServiceStatus, setEmailServiceStatus] = useState(null);
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');
    setResetLink('');

    try {
      const response = await axios.post(`${API_BASE_URL}/auth/forgot-password`, { email });
      const emailSent = response?.data?.emailSent;
      const link = response?.data?.resetLink;

      if (emailSent === false && link) {
        setSuccess('Email delivery is blocked on the server. Use the secure reset link below to continue.');
        setResetLink(link);
      } else if (emailSent === true) {
        // User-friendly success message
        let successMessage = 'Password reset link has been sent to your email address. Please check your inbox and spam folder.';

        setSuccess(successMessage);
      } else {
        setSuccess('Password reset link has been sent to your email address. Please check your inbox and spam folder.');
      }
      setSubmitted(true);
    } catch (err) {
      if (err.response?.status === 404) {
        setError('No account found with this email address. Please check your email or register a new account.');
      } else if (err.response?.status === 503) {
        const hint = err.response?.data?.hint || '';
        setError(`Email service is not configured on the server. ${hint}`);
      } else if (err.response?.status === 500) {
        setError('Email service is currently unavailable. Please try again later or contact support.');
      } else if (err.response?.status === 429) {
        setError('Too many requests. Please wait a moment before trying again.');
      } else {
        setError(err.response?.data?.message || 'An error occurred. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    if (error || success) {
      setError('');
      setSuccess('');
      setResetLink('');
    }
  };

  const copyLink = async () => {
    if (!resetLink) return;
    try {
      await navigator.clipboard.writeText(resetLink);
      alert('Reset link copied to clipboard');
    } catch (e) {
      // no-op
    }
  };

  return (
    <div className="forgot-page">
      <div className="forgot-background" aria-hidden="true" />
      <Container fluid className="forgot-container">
        <Row className="g-0 forgot-row">
          {/* Left: dark info panel (theme) */}
          <Col xs={12} lg={5} className="forgot-features-panel order-2 order-lg-0">
            <div className="forgot-features-content">
              <div className="forgot-features-logo">
                <img src="/logonew.png" alt="Continuum Logo" className="forgot-logo-image" />
              </div>
              <div className="forgot-features-header">
                <h1 className="forgot-features-title">Having trouble signing in?</h1>
                <div className="forgot-features-divider" />
              </div>
              <div className="forgot-features-body">
                <p className="forgot-features-desc">
                  Enter your email and we'll send you a secure link to reset your password. If you don't see it, check your spam folder.
                </p>
                <p className="forgot-features-note">
                  We use reliable email delivery to ensure your reset link reaches you quickly and securely.
                </p>
              </div>
            </div>
          </Col>

          {/* Right: white form panel */}
          <Col xs={12} lg={7} className="forgot-form-panel order-1 order-lg-0">
            <div className="forgot-form-inner">
              <h2 className="forgot-form-title">Reset Password</h2>
              <p className="forgot-form-subtitle">We'll send a reset link to your email</p>

              {!submitted && !success && (
                <Form onSubmit={handleSubmit} className="forgot-form">
                  {error && <Alert variant="danger" className="forgot-alert">{error}</Alert>}
                  {emailServiceStatus && (
                    <Alert
                      variant={emailServiceStatus.microsoftGraphConfigured || emailServiceStatus.smtpConfigured ? "success" : "warning"}
                      className="forgot-alert"
                    >
                      <div className="fw-bold mb-2">Email Service Status:</div>
                      <div>Microsoft Graph: {emailServiceStatus.microsoftGraphConfigured ? '✅ Configured' : '❌ Not configured'}</div>
                      <div>SMTP Fallback: {emailServiceStatus.smtpConfigured ? '✅ Configured' : '❌ Not configured'}</div>
                      {emailServiceStatus.userEmail && <div>User Email: {emailServiceStatus.userEmail}</div>}
                    </Alert>
                  )}
                  <Form.Group className="mb-3">
                    <Form.Label className="forgot-label">Email Address</Form.Label>
                    <Form.Control
                      type="email"
                      value={email}
                      onChange={handleEmailChange}
                      required
                      autoFocus
                      placeholder="Enter your email address"
                      disabled={loading}
                      className="forgot-input"
                    />
                  </Form.Group>
                  <div className="d-flex flex-column gap-2 w-100">
                    <button
                      type="submit"
                      disabled={loading || !email.trim()}
                      className="forgot-btn forgot-btn-primary"
                    >
                      {loading ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          Sending...
                        </>
                      ) : (
                        'Send Reset Link'
                      )}
                    </button>
                    <p className="forgot-back-wrap mb-0 mt-2 text-center">
                      <Link to="/login" className="forgot-back-link">Back to Login</Link>
                    </p>
                  </div>
                  <p className="forgot-register-wrap text-center text-muted mt-3 mb-0">
                    Don't have an account? <Link to="/register" className="forgot-register-link">Create one here</Link>
                  </p>
                </Form>
              )}

              {submitted && (
                <div className="forgot-success-block">
                  {success && <Alert variant="success" className="forgot-alert">{success}</Alert>}
                  {resetLink && (
                    <>
                      <Alert variant="info" className="forgot-alert">
                        Use this link to reset now: <a href={resetLink} target="_blank" rel="noreferrer" className="forgot-reset-link">Open reset link</a>
                      </Alert>
                      <div className="d-flex justify-content-center gap-2 mb-3">
                        <Button variant="outline-secondary" onClick={copyLink} size="sm" className="forgot-btn forgot-btn-outline">Copy Link</Button>
                        <Button variant="primary" as="a" href={resetLink} target="_blank" rel="noreferrer" size="sm" className="forgot-btn forgot-btn-primary">Open Link</Button>
                      </div>
                    </>
                  )}
                  <div className="d-flex flex-column gap-2 w-100">
                    <button
                      type="button"
                      onClick={() => {
                        setEmail('');
                        setError('');
                        setSuccess('');
                        setSubmitted(false);
                        setResetLink('');
                        setEmailServiceStatus(null);
                      }}
                      className="forgot-btn forgot-btn-outline"
                    >
                      Reset Another Email
                    </button>
                    <p className="forgot-back-wrap mb-0 mt-2 text-center">
                      <Link to="/login" className="forgot-back-link">Back to Login</Link>
                    </p>
                  </div>
                </div>
              )}
            </div>
          </Col>
        </Row>
      </Container>
      <Footer />
    </div>
  );
};

export default ForgotPassword;
